import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Card, CardContent } from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ScaleIcon from "@mui/icons-material/Scale";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import api from "../../services/api";
import StatsCard from "../../components/admin/StatsCard";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";

const PIE_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#8B4513"];

function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await api.get("/api/dashboard/summary");
        setSummary(response.data);
      } catch (err) {
        setError("데이터를 불러오는 데 실패했습니다.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
          대시보드
        </Typography>
        <LoadingSkeleton variant="cards" />
        <Box sx={{ mt: 3 }}>
          <LoadingSkeleton variant="chart" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 4 }}>
        {error}
      </Typography>
    );
  }

  if (!summary) return null;

  // monthlyCollectionTrend: [{ period, weights: { total, pet, aluminum, mixed, paper } }]
  const monthlyTrend = (summary.monthlyCollectionTrend || []).map((item) => ({
    period: item.period,
    total: item.weights?.total ?? 0,
    pet: item.weights?.pet ?? 0,
    aluminum: item.weights?.aluminum ?? 0,
    mixed: item.weights?.mixed ?? 0,
    paper: item.weights?.paper ?? 0,
  }));

  // materialBreakdown: { total, pet, aluminum, mixed, paper }
  const mb = summary.materialBreakdown;
  const pieData = mb
    ? [
        { name: "PET", value: mb.pet || 0 },
        { name: "알루미늄", value: mb.aluminum || 0 },
        { name: "혼합", value: mb.mixed || 0 },
        { name: "파지", value: mb.paper || 0 },
      ].filter((d) => d.value > 0)
    : [];

  // userRegistrationTrend: [{ period, count }]
  const registrationTrend = summary.userRegistrationTrend || [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        대시보드
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="총 가입자 수"
            value={`${summary.totalUserCount ?? 0}명`}
            icon={PeopleIcon}
            color="#2a9ac6"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="총 수거자 수"
            value={`${summary.totalCollectorCount ?? 0}명`}
            icon={LocalShippingIcon}
            color="#27ae60"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="당월 총 수거량"
            value={`${(summary.monthlyCollectionWeight ?? 0).toFixed(1)} kg`}
            icon={ScaleIcon}
            color="#8884d8"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="대기 중인 요청"
            value={`${summary.pendingRequestCount ?? 0}건`}
            icon={PendingActionsIcon}
            color="#f39c12"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Monthly Collection Trend - Line Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                월별 수거량 추이 (최근 6개월)
              </Typography>
              <Box sx={{ height: 320 }}>
                {monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => {
                          const parts = val.split("-");
                          return parts.length >= 2
                            ? `${parts[0].slice(2)}/${parts[1]}`
                            : val;
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        label={{
                          value: "kg",
                          angle: -90,
                          position: "insideLeft",
                          style: { fontSize: 12 },
                        }}
                      />
                      <Tooltip
                        formatter={(value) => `${Number(value).toFixed(1)} kg`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        name="총 수거량"
                        stroke="#2a9ac6"
                        strokeWidth={3}
                        dot={{ r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart message="트렌드 데이터가 없습니다." />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Material Breakdown Pie Chart */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                소재별 수거 비율
              </Typography>
              <Box sx={{ height: 320 }}>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        outerRadius={90}
                        innerRadius={45}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine
                      >
                        {pieData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `${Number(value).toFixed(1)} kg`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart message="소재별 데이터가 없습니다." />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Registration Trend */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                신규 가입자 추이 (최근 6개월)
              </Typography>
              <Box sx={{ height: 280 }}>
                {registrationTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={registrationTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => {
                          const parts = val.split("-");
                          return parts.length >= 2
                            ? `${parts[0].slice(2)}/${parts[1]}`
                            : val;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar
                        dataKey="count"
                        name="가입자 수"
                        fill="#2a9ac6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart message="가입자 추이 데이터가 없습니다." />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Material Breakdown Stacked Bar */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                소재별 월간 수거량
              </Typography>
              <Box sx={{ height: 280 }}>
                {monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => {
                          const parts = val.split("-");
                          return parts.length >= 2
                            ? `${parts[0].slice(2)}/${parts[1]}`
                            : val;
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        label={{
                          value: "kg",
                          angle: -90,
                          position: "insideLeft",
                          style: { fontSize: 12 },
                        }}
                      />
                      <Tooltip
                        formatter={(value) => `${Number(value).toFixed(1)} kg`}
                      />
                      <Legend />
                      <Bar
                        dataKey="pet"
                        name="PET"
                        stackId="a"
                        fill="#8884d8"
                      />
                      <Bar
                        dataKey="aluminum"
                        name="알루미늄"
                        stackId="a"
                        fill="#82ca9d"
                      />
                      <Bar
                        dataKey="mixed"
                        name="혼합"
                        stackId="a"
                        fill="#ffc658"
                      />
                      <Bar
                        dataKey="paper"
                        name="파지"
                        stackId="a"
                        fill="#8B4513"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart message="소재별 추이 데이터가 없습니다." />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// 빈 차트 상태 표시용 내부 컴포넌트
function EmptyChart({ message }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <Typography color="text.secondary">{message}</Typography>
    </Box>
  );
}

export default DashboardPage;
